/*
 * DownloadCTA — bloque de descarga/instalación para la web pública (Login).
 *
 * Site Factory es una PWA (no vive en App Store/Play Store), así que la
 * "descarga" real es "instalar como aplicación desde el navegador":
 *   - Android / Chrome / Edge → botón nativo (usa beforeinstallprompt).
 *   - iPhone / iPad Safari → añadir a pantalla de inicio (paso a paso).
 *   - macOS / Windows → botón nativo si el navegador lo soporta, sino instrucciones.
 *
 * Muestra 3 tarjetas y un botón "Instalar ahora" cuando el navegador es
 * elegible. Si el usuario ya está en la app instalada, se oculta el bloque.
 */
import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Monitor, Apple, Download, Share, Plus, X, Check, QrCode } from 'lucide-react';

const isIOS = () => typeof navigator !== 'undefined'
  && /iP(hone|ad|od)/.test(navigator.userAgent)
  && !window.MSStream;
const isAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
const isStandalone = () => (typeof window !== 'undefined')
  && (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true);

const Step = ({ n, children }) => (
  <li className="flex gap-2 text-sm text-white/80">
    <span className="text-[#A3FF12] font-black flex-shrink-0">{n}.</span>
    <span>{children}</span>
  </li>
);

const IOSInstructions = ({ onClose }) => (
  <div className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur flex items-end sm:items-center justify-center p-4"
    onClick={onClose} data-testid="ios-install-modal">
    <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/15 rounded-2xl w-full max-w-sm p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">INSTALAR EN iPHONE / iPAD</div>
          <h3 className="text-white font-black text-lg mt-1">Añadir a pantalla de inicio</h3>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Cerrar"><X size={20}/></button>
      </div>
      <ol className="space-y-3">
        <Step n={1}>Abre esta web en <b>Safari</b> (no en Chrome).</Step>
        <Step n={2}>Pulsa el botón <Share size={14} className="inline mx-1 text-blue-400"/> <b>Compartir</b> abajo.</Step>
        <Step n={3}>Baja y elige <Plus size={14} className="inline mx-1 text-white/70"/> <b>«Añadir a pantalla de inicio»</b>.</Step>
        <Step n={4}>Pulsa <b>«Añadir»</b> arriba a la derecha. Listo.</Step>
      </ol>
      <p className="text-white/40 text-[11px] mt-4">La app se abre a pantalla completa, sin barra de navegación, como una app nativa.</p>
    </div>
  </div>
);

const AndroidInstructions = ({ onClose }) => (
  <div className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur flex items-end sm:items-center justify-center p-4"
    onClick={onClose} data-testid="android-install-modal">
    <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/15 rounded-2xl w-full max-w-sm p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">INSTALAR EN ANDROID</div>
          <h3 className="text-white font-black text-lg mt-1">Instalar Adquiriam</h3>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Cerrar"><X size={20}/></button>
      </div>
      <ol className="space-y-3">
        <Step n={1}>Abre esta web en <b>Chrome</b>.</Step>
        <Step n={2}>Pulsa el menú <b>⋮</b> arriba a la derecha.</Step>
        <Step n={3}>Elige <b>«Instalar app»</b> o <b>«Añadir a pantalla de inicio»</b>.</Step>
        <Step n={4}>Acepta. Aparecerá con su icono verde en el escritorio.</Step>
      </ol>
    </div>
  </div>
);

const DownloadCTA = ({ forceShow = false }) => {
  const [canPrompt, setCanPrompt] = useState(!!(typeof window !== 'undefined' && window.__deferredInstallPrompt));
  const [installed, setInstalled] = useState(false);
  const [running, setRunning] = useState(isStandalone());
  const [showIos, setShowIos] = useState(false);
  const [showAndroid, setShowAndroid] = useState(false);

  useEffect(() => {
    const onAvail = () => setCanPrompt(true);
    const onInstalled = () => { setInstalled(true); setRunning(true); setCanPrompt(false); };
    window.addEventListener('sf:install-available', onAvail);
    window.addEventListener('sf:installed', onInstalled);
    return () => {
      window.removeEventListener('sf:install-available', onAvail);
      window.removeEventListener('sf:installed', onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    const p = typeof window !== 'undefined' ? window.__deferredInstallPrompt : null;
    if (!p) {
      // Sin prompt disponible → mostrar guía según plataforma
      if (isIOS()) setShowIos(true);
      else setShowAndroid(true);
      return;
    }
    p.prompt();
    try { await p.userChoice; } catch { /* noop */ }
    window.__deferredInstallPrompt = null;
    setCanPrompt(false);
  };

  if (running && !forceShow) return null; // ya está corriendo como app instalada (salvo que se fuerce, ej. dentro de la app)

  // URL canónica para el QR (el móvil abre directamente el login)
  const qrTarget = typeof window !== 'undefined' ? `${window.location.origin}/login` : '';

  return (
    <div className="w-full max-w-md mx-auto mt-8" data-testid="download-cta">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#A3FF12]/10 rounded-full blur-3xl pointer-events-none"/>
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <Download size={16} className="text-[#A3FF12]"/>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">DESCARGA LA APP</div>
          </div>
          <h3 className="text-white text-xl font-black leading-tight">Instala Adquiriam en tu dispositivo</h3>
          <p className="text-white/50 text-sm mt-1">Se abre como una app nativa. Sin App Store ni Play Store.</p>

          {installed && (
            <div className="mt-4 flex items-center gap-2 bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] rounded-xl px-3 py-2 text-sm font-bold" data-testid="download-installed-banner">
              <Check size={16}/> ¡Instalada! Búscala en tu pantalla de inicio.
            </div>
          )}

          {/* --- QR + prompt ---  */}
          <div className="mt-5 grid grid-cols-[auto_1fr] gap-4 items-center">
            <div className="bg-white p-2 rounded-xl shadow-lg" data-testid="download-qr">
              <QRCodeSVG value={qrTarget} size={112} level="M" bgColor="#ffffff" fgColor="#000000"
                imageSettings={{
                  src: '/icons/icon-192.png',
                  height: 22,
                  width: 22,
                  excavate: true,
                }}/>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[10px] font-black tracking-widest text-[#A3FF12] mb-1">
                <QrCode size={12}/> ESCANEA CON EL MÓVIL
              </div>
              <p className="text-white/70 text-xs leading-snug">
                Apunta con la cámara para abrir Adquiriam en tu teléfono y añadirla a la pantalla de inicio.
              </p>
              {!installed && canPrompt && (
                <button onClick={promptInstall} data-testid="btn-download-install"
                  className="mt-3 inline-flex items-center justify-center gap-2 bg-[#A3FF12] text-black font-black py-2 px-3 rounded-lg hover:bg-[#b8ff3a] transition-colors text-xs">
                  <Download size={14}/> Instalar aquí
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
            <button onClick={() => setShowIos(true)} data-testid="btn-download-ios"
              className="flex items-center gap-2 justify-center bg-black/40 hover:bg-white/5 border border-white/10 hover:border-[#A3FF12]/40 rounded-xl px-3 py-3 text-white text-sm font-bold transition-colors">
              <Apple size={16} className="text-white/70"/> iPhone / iPad
            </button>
            <button onClick={() => setShowAndroid(true)} data-testid="btn-download-android"
              className="flex items-center gap-2 justify-center bg-black/40 hover:bg-white/5 border border-white/10 hover:border-[#A3FF12]/40 rounded-xl px-3 py-3 text-white text-sm font-bold transition-colors">
              <Smartphone size={16} className="text-white/70"/> Android
            </button>
            <button onClick={promptInstall} data-testid="btn-download-desktop"
              className="flex items-center gap-2 justify-center bg-black/40 hover:bg-white/5 border border-white/10 hover:border-[#A3FF12]/40 rounded-xl px-3 py-3 text-white text-sm font-bold transition-colors">
              <Monitor size={16} className="text-white/70"/> Ordenador
            </button>
          </div>

          <p className="text-white/30 text-[11px] mt-3 text-center">
            Funciona con y sin conexión · Sincroniza los datos automáticamente
          </p>
        </div>
      </div>
      {showIos && <IOSInstructions onClose={() => setShowIos(false)}/>}
      {showAndroid && <AndroidInstructions onClose={() => setShowAndroid(false)}/>}
    </div>
  );
};

export default DownloadCTA;
